import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UpdateAccountFieldName } from "../../types/enums/UpdateAccountField";
import type { LinkedAccountDTO } from "../../types/dtos";
import type { UpdateLinkedAccountViewModel } from "../../types/viewModels/LinkAccountViewModel";
import { useApiAccess } from "@repo/hooks/contexts";
import { linkedAccountKeys } from "@repo/types/queries";
import type { ExecutionResponse } from "@repo/types/responses";

export function useGetLinkedAccounts<T = LinkedAccountDTO>(
  select?: (data: LinkedAccountDTO[]) => T[],
) {
  const { apiClient } = useApiAccess();

  return useQuery<LinkedAccountDTO[], Error, T[]>({
    queryKey: linkedAccountKeys.list(),
    queryFn: async () => {
      const response =
        await apiClient.get<LinkedAccountDTO[]>(`/LinkedAccount/Get`);

      return response.data;
    },
    select,
  });
}

export function useUpdateLinkedAccount() {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (model: UpdateLinkedAccountViewModel) => {
      const response = await apiClient.post<ExecutionResponse>(
        `/LinkedAccount/Update`,
        model,
      );
      return response.data;
    },
    onSuccess: (_data, model) => {
      if (!model.AccountId) return;

      linkedAccountKeys.invalidateList(queryClient);

      if (
        model.UpdateAccountField === UpdateAccountFieldName.BgColorHex ||
        model.UpdateAccountField === UpdateAccountFieldName.TextColorHex
      ) {
        linkedAccountKeys.invalidateDetails(queryClient, model.AccountId);
      }
    },
  });
}
